import useLocale from 'next-translate/useTranslation';
import { memo, useEffect, useRef } from 'react';

import styles from './ImageGenerationPreview.module.css';

// All visible generation timers share one clock. Nothing runs without subscribers.
const subscribers = new Set<() => void>();
let interval: ReturnType<typeof setInterval> | undefined;

function stopClock() {
  if (interval !== undefined) clearInterval(interval);
  interval = undefined;
}

function tick() {
  subscribers.forEach((update) => update());
}

function syncClock() {
  stopClock();
  if (!document.hidden && subscribers.size > 0) {
    tick();
    interval = setInterval(tick, 1000);
  }
}

function subscribe(update: () => void) {
  subscribers.add(update);
  if (subscribers.size === 1) {
    document.addEventListener('visibilitychange', syncClock);
    syncClock();
  } else {
    update();
  }

  return () => {
    subscribers.delete(update);
    if (subscribers.size === 0) {
      stopClock();
      document.removeEventListener('visibilitychange', syncClock);
    }
  };
}

const GenerationElapsedTime = memo(function GenerationElapsedTime({
  startedAt,
  running,
}: {
  startedAt?: number;
  running: boolean;
}) {
  const { t } = useLocale('ai-images');
  const textRef = useRef<HTMLSpanElement>(null);
  const fallbackStartRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!running) return;
    const text = textRef.current;
    if (!text) return;

    fallbackStartRef.current ??= Date.now();
    const start =
      typeof startedAt === 'number' && Number.isFinite(startedAt)
        ? startedAt
        : fallbackStartRef.current;

    return subscribe(() => {
      // Derive from the timestamp so background throttling cannot cause drift.
      const seconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const value = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
      if (text.textContent !== value) text.textContent = value;
    });
  }, [startedAt, running]);

  return (
    <span
      className={styles.elapsed}
      role='timer'
      aria-label={t('Elapsed time')}
      aria-live='off'
    >
      {/* This empty leaf is owned by the effect; React never reconciles its text.
          Clock ticks don't set state or re-render the badge, card, or canvas. */}
      <span ref={textRef} />
    </span>
  );
});

export default GenerationElapsedTime;
